import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BottomNavigation from '../components/BottomNavigation';
import './QuizAttempt.css';

interface QuestionMedia {
  type: 'image' | 'video' | 'audio';
  url: string;
}

interface MultipleChoiceQuestion {
  id: number;
  type: 'multiple_choice';
  question: string;
  options: string[];
  correctAnswer: number[];
  score: number;
  media: QuestionMedia | null;
}

interface TrueFalseQuestion {
  id: number;
  type: 'true_false';
  question: string;
  correctAnswer: boolean;
  score: number;
  media: QuestionMedia | null;
}

interface FillBlankQuestion {
  id: number;
  type: 'fill_blank';
  question: string;
  correctAnswer: string;
  score: number;
  media: QuestionMedia | null;
}

type Question = MultipleChoiceQuestion | TrueFalseQuestion | FillBlankQuestion;

const QuizAttempt: React.FC = () => {
  const navigate = useNavigate();
  const { id, chapterId, quizId } = useParams();
  const [currentSection, setCurrentSection] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<{ [key: number]: any }>({});
  const [timeRemaining, setTimeRemaining] = useState(900); // 15 minutes

  // Mock data
  const quiz = {
    id: 1,
    title: 'Chapter Quiz',
    type: 'educational', // or 'normal'
    totalScore: 100,
    sections: [
      {
        id: 1,
        title: 'Multiple Choice Questions',
        description: 'Choose the correct answer for each question',
        media: { type: 'audio', url: 'audio.mp3' },
        questions: [
          {
            id: 1,
            type: 'multiple_choice',
            question: 'What is the derivative of x²?',
            options: ['x', '2x', 'x²', '2x²'],
            correctAnswer: [1],
            score: 10,
            media: null
          },
          {
            id: 2,
            type: 'multiple_choice',
            question: 'Which of the following is a limit?',
            options: ['Derivative', 'Integral', 'Limit', 'Function'],
            correctAnswer: [2],
            score: 10,
            media: null
          }
        ]
      },
      {
        id: 2,
        title: 'True or False',
        description: 'Determine if each statement is true or false',
        media: null,
        questions: [
          {
            id: 3,
            type: 'true_false',
            question: 'Calculus is a branch of mathematics',
            correctAnswer: true,
            score: 10,
            media: null
          }
        ]
      },
      {
        id: 3,
        title: 'Fill in the Blanks',
        description: 'Complete the following sentences',
        media: null,
        questions: [
          {
            id: 4,
            type: 'fill_blank',
            question: 'The derivative of a constant is ___',
            correctAnswer: 'zero',
            score: 10,
            media: null
          }
        ]
      }
    ]
  };

  const currentSectionData = quiz.sections[currentSection];
  const currentQuestionData = currentSectionData.questions[currentQuestion];

  const handleAnswerChange = (questionId: number, answer: any) => {
    setAnswers({
      ...answers,
      [questionId]: answer
    });
  };

  const handleNext = () => {
    if (currentQuestion < currentSectionData.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else if (currentSection < quiz.sections.length - 1) {
      setCurrentSection(currentSection + 1);
      setCurrentQuestion(0);
    } else {
      handleSubmit();
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    } else if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
      setCurrentQuestion(quiz.sections[currentSection - 1].questions.length - 1);
    }
  };

  const handleSubmit = () => {
    navigate(`/course/${id}/chapter/${chapterId}/quiz/${quizId}/results`, {
      state: { answers, quiz }
    });
  };

  const getTotalQuestions = () => {
    return quiz.sections.reduce((total, section) => total + section.questions.length, 0);
  };

  const getCurrentQuestionNumber = () => {
    let number = 0;
    for (let i = 0; i < currentSection; i++) {
      number += quiz.sections[i].questions.length;
    }
    return number + currentQuestion + 1;
  };

  return (
    <div className="quiz-attempt-page">
      <header className="quiz-attempt-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          ←
        </button>
        <div className="quiz-header-info">
          <h1>{quiz.title}</h1>
          <div className="quiz-progress-info">
            <span>Question {getCurrentQuestionNumber()} of {getTotalQuestions()}</span>
            <span className="time-remaining">⏱️ {Math.floor(timeRemaining / 60)}:{(timeRemaining % 60).toString().padStart(2, '0')}</span>
          </div>
        </div>
        <div className="header-spacer"></div>
      </header>

      <div className="quiz-attempt-content">
        {/* Section Header */}
        <div className="section-header-card">
          <h2 className="section-title-quiz">{currentSectionData.title}</h2>
          <p className="section-description">{currentSectionData.description}</p>
          {currentSectionData.media && (
            <div className="section-media">
              {currentSectionData.media.type === 'audio' && (
                <button className="play-audio-button">🔊 Play Audio</button>
              )}
            </div>
          )}
        </div>

        {/* Question Card */}
        <div className="question-card">
          <div className="question-header">
            <span className="question-number">Q{getCurrentQuestionNumber()}</span>
            <span className="question-score">{currentQuestionData.score} points</span>
          </div>

          <div className="question-content">
            {currentQuestionData.media && (() => {
              const media = currentQuestionData.media as QuestionMedia;
              return (
                <div className="question-media">
                  {media.type === 'image' && (
                    <img src={media.url} alt="Question" />
                  )}
                  {media.type === 'video' && (
                    <div className="media-placeholder">🎥 Video</div>
                  )}
                </div>
              );
            })()}

            <h3 className="question-text">{currentQuestionData.question}</h3>

            {/* Answer Options */}
            <div className="answer-options">
              {currentQuestionData.type === 'multiple_choice' && (
                <div className="multiple-choice-options">
                  {(currentQuestionData as MultipleChoiceQuestion).options.map((option: string, index: number) => (
                    <label
                      key={index}
                      className={`option-label ${answers[currentQuestionData.id] === index ? 'selected' : ''}`}
                    >
                      <input
                        type="radio"
                        name={`question-${currentQuestionData.id}`}
                        value={index}
                        checked={answers[currentQuestionData.id] === index}
                        onChange={() => handleAnswerChange(currentQuestionData.id, index)}
                      />
                      <span className="option-text">{option}</span>
                    </label>
                  ))}
                </div>
              )}

              {currentQuestionData.type === 'true_false' && (
                <div className="true-false-options">
                  <label
                    className={`option-label ${answers[currentQuestionData.id] === true ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name={`question-${currentQuestionData.id}`}
                      checked={answers[currentQuestionData.id] === true}
                      onChange={() => handleAnswerChange(currentQuestionData.id, true)}
                    />
                    <span className="option-text">True</span>
                  </label>
                  <label
                    className={`option-label ${answers[currentQuestionData.id] === false ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name={`question-${currentQuestionData.id}`}
                      checked={answers[currentQuestionData.id] === false}
                      onChange={() => handleAnswerChange(currentQuestionData.id, false)}
                    />
                    <span className="option-text">False</span>
                  </label>
                </div>
              )}

              {currentQuestionData.type === 'fill_blank' && (
                <div className="fill-blank-input">
                  <input
                    type="text"
                    value={answers[currentQuestionData.id] || ''}
                    onChange={(e) => handleAnswerChange(currentQuestionData.id, e.target.value)}
                    placeholder="Enter your answer"
                    className="blank-input"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="quiz-navigation">
          <button
            className="nav-button prev-button"
            onClick={handlePrevious}
            disabled={currentSection === 0 && currentQuestion === 0}
          >
            ← Previous
          </button>
          <button className="nav-button next-button" onClick={handleNext}>
            {getCurrentQuestionNumber() === getTotalQuestions() ? 'Submit' : 'Next →'}
          </button>
        </div>

        {/* Progress Indicator */}
        <div className="quiz-progress-indicator">
          {quiz.sections.map((section, sIndex) =>
            section.questions.map((_, qIndex) => {
              const qNum = quiz.sections.slice(0, sIndex).reduce((sum, s) => sum + s.questions.length, 0) + qIndex + 1;
              const isAnswered = answers[section.questions[qIndex].id] !== undefined;
              const isCurrent = sIndex === currentSection && qIndex === currentQuestion;
              
              return (
                <div
                  key={`${sIndex}-${qIndex}`}
                  className={`progress-dot ${isCurrent ? 'current' : ''} ${isAnswered ? 'answered' : ''}`}
                  onClick={() => {
                    setCurrentSection(sIndex);
                    setCurrentQuestion(qIndex);
                  }}
                />
              );
            })
          )}
        </div>
      </div>

      <BottomNavigation />
    </div>
  );
};

export default QuizAttempt;
